import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useUpdateInfraItemVariant,
  useCreateInfraItemVariant,
  useListInfraAttributeValues,
  getListInfraItemVariantsQueryKey,
  getListInfraItemsQueryKey,
  getListInfraAttributeValuesQueryKey,
} from "@workspace/api-client-react";
import type { InfraItemVariant, InfraItem } from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { InfraAttributeKeyCombobox } from "./infra-attribute-key-combobox";
import { InfraAttributeValueCombobox } from "./infra-attribute-value-combobox";

const GST_RATES = [0, 5, 12, 18, 28] as const;

interface InfraVariantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: number;
  parentItem: InfraItem;
  /** Omit (or pass null) to open in create mode. */
  variant?: InfraItemVariant | null;
}

export function InfraVariantDialog({
  open,
  onOpenChange,
  itemId,
  parentItem,
  variant,
}: InfraVariantDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const updateMutation = useUpdateInfraItemVariant();
  const createMutation = useCreateInfraItemVariant();
  const isCreate = variant == null;

  const [attributeKeyId, setAttributeKeyId] = useState<number | null>(null);
  const [attributeValueId, setAttributeValueId] = useState<number | null>(
    variant?.attributeValueId ?? null,
  );
  const [itemCode, setItemCode] = useState(variant?.itemCode ?? "");
  const [hsnCode, setHsnCode] = useState(variant?.hsnCode ?? "");
  const [gstRateStr, setGstRateStr] = useState<string>(
    variant?.gstRate != null ? String(variant.gstRate) : "__inherit__",
  );
  const [unitPriceStr, setUnitPriceStr] = useState<string>(
    variant?.unitPrice != null ? String(variant.unitPrice) : "",
  );
  const [status, setStatus] = useState<"Active" | "Discontinued">(
    (variant?.status as "Active" | "Discontinued") ?? "Active",
  );

  // Load all infra attribute values (no key filter) to reverse-lookup the
  // variant's attributeKeyId from its attributeValueId on edit open.
  const { data: allValuesUnfiltered = [] } = useListInfraAttributeValues(
    undefined,
    {
      query: {
        queryKey: getListInfraAttributeValuesQueryKey(),
      },
    },
  );

  useEffect(() => {
    if (attributeKeyId != null) return;
    const avId = variant?.attributeValueId;
    if (avId == null) return;
    const av = allValuesUnfiltered.find((v) => v.id === avId);
    if (av) setAttributeKeyId(av.attributeKeyId);
  }, [variant?.attributeValueId, allValuesUnfiltered, attributeKeyId]);

  // Filtered values for the selected key (for display name preview)
  const { data: keyValues = [] } = useListInfraAttributeValues(
    attributeKeyId != null ? { attributeKeyId, status: "Active" } : undefined,
    {
      query: {
        queryKey: getListInfraAttributeValuesQueryKey(
          attributeKeyId != null ? { attributeKeyId } : undefined,
        ),
        enabled: attributeKeyId != null,
      },
    },
  );

  const selectedValueText =
    keyValues.find((v) => v.id === attributeValueId)?.value ?? "";

  const displayNamePreview = selectedValueText
    ? `${parentItem.name} - ${selectedValueText}`
    : variant?.displayName ?? `${parentItem.name} - (pick a value)`;

  // Resolved effective values (variant override ?? parent fallback)
  const effectiveHsn = hsnCode.trim() || parentItem.hsnCode || "—";
  const effectiveGst =
    gstRateStr !== "__inherit__"
      ? `${gstRateStr}%`
      : parentItem.gstRate != null
        ? `${parentItem.gstRate}%`
        : "—";
  const effectivePrice =
    unitPriceStr.trim()
      ? `₹${Number(unitPriceStr).toLocaleString("en-IN")}`
      : parentItem.unitPrice != null
        ? `₹${Number(parentItem.unitPrice).toLocaleString("en-IN")} (parent)`
        : "—";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const attributeValueIds =
      attributeValueId != null ? [attributeValueId] : undefined;
    if (isCreate && !attributeValueIds) {
      toast({
        title: "Pick an attribute value for the variant.",
        variant: "destructive",
      });
      return;
    }

    const data = {
      attributeValueIds,
      itemCode: itemCode.trim() || null,
      hsnCode: hsnCode.trim() || null,
      gstRate: gstRateStr !== "__inherit__" ? Number(gstRateStr) : null,
      unitPrice: unitPriceStr.trim() !== "" ? Number(unitPriceStr) : null,
      status,
    };

    try {
      if (isCreate) {
        await createMutation.mutateAsync({ itemId, data });
      } else {
        await updateMutation.mutateAsync({
          itemId,
          variantId: variant!.id,
          data,
        });
      }
      queryClient.invalidateQueries({
        queryKey: getListInfraItemVariantsQueryKey(itemId),
      });
      queryClient.invalidateQueries({
        queryKey: getListInfraItemsQueryKey(),
      });
      toast({ title: isCreate ? "Variant added." : "Variant updated." });
      onOpenChange(false);
    } catch (err: unknown) {
      const s = (err as { status?: number })?.status;
      if (s === 409) {
        toast({
          title: "This attribute already has a variant on this item.",
          variant: "destructive",
        });
      } else if (s === 422) {
        toast({
          title: "Attribute value belongs to a different space.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Failed to save variant. Please try again.",
          variant: "destructive",
        });
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isCreate ? "Add Infra Variant" : "Edit Infra Variant"}</DialogTitle>
        </DialogHeader>

        <div key={variant?.id ?? "new"}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Attribute Key */}
            <div className="space-y-2">
              <Label>Attribute Key</Label>
              <InfraAttributeKeyCombobox
                value={attributeKeyId}
                onChange={(id) => {
                  setAttributeKeyId(id);
                  setAttributeValueId(null);
                }}
              />
            </div>

            {/* Attribute Value */}
            <div className="space-y-2">
              <Label>Attribute Value</Label>
              <InfraAttributeValueCombobox
                attributeKeyId={attributeKeyId}
                value={attributeValueId}
                onChange={(id) => setAttributeValueId(id)}
                disabled={attributeKeyId == null}
              />
            </div>

            {/* Item Code */}
            <div className="space-y-2">
              <Label>Item Code</Label>
              <Input
                placeholder="Optional per-variant code"
                value={itemCode}
                onChange={(e) => setItemCode(e.target.value)}
              />
            </div>

            {/* HSN Code */}
            <div className="space-y-2">
              <Label>
                HSN Code{" "}
                <span className="text-xs text-muted-foreground">
                  (blank = inherit parent)
                </span>
              </Label>
              <Input
                placeholder={`Inherits: ${parentItem.hsnCode ?? "—"}`}
                value={hsnCode}
                onChange={(e) => setHsnCode(e.target.value)}
              />
            </div>

            {/* GST Rate */}
            <div className="space-y-2">
              <Label>
                GST Rate{" "}
                <span className="text-xs text-muted-foreground">
                  (blank = inherit parent)
                </span>
              </Label>
              <Select value={gstRateStr} onValueChange={setGstRateStr}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__inherit__">
                    Inherits parent ({parentItem.gstRate ?? "—"}%)
                  </SelectItem>
                  {GST_RATES.map((r) => (
                    <SelectItem key={r} value={String(r)}>
                      {r}%
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Unit Price */}
            <div className="space-y-2">
              <Label>
                Unit Price (₹){" "}
                <span className="text-xs text-muted-foreground">
                  (blank = inherit parent)
                </span>
              </Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                placeholder={
                  parentItem.unitPrice != null
                    ? `Inherits: ₹${Number(parentItem.unitPrice).toLocaleString("en-IN")}`
                    : "Inherits parent"
                }
                value={unitPriceStr}
                onChange={(e) => setUnitPriceStr(e.target.value)}
              />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={status}
                onValueChange={(v) =>
                  setStatus(v as "Active" | "Discontinued")
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Discontinued">Discontinued</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Resolved values preview */}
            <div className="rounded-md bg-muted px-3 py-2 text-sm space-y-1">
              <p className="font-medium text-foreground">Effective values</p>
              <p className="text-muted-foreground">
                Display name: {displayNamePreview}
              </p>
              <p className="text-muted-foreground">
                HSN: {effectiveHsn} · GST: {effectiveGst} · Price:{" "}
                {effectivePrice}
              </p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isCreate ? createMutation.isPending : updateMutation.isPending}
              >
                {isCreate
                  ? createMutation.isPending
                    ? "Adding…"
                    : "Add Variant"
                  : updateMutation.isPending
                    ? "Saving…"
                    : "Save Variant"}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
